'use client';
import Link from 'next/link';
import Navbar from '@/components/ui/Navbar';
import Footer from '@/components/ui/Footer';

const SITE_NAME = 'RePrint Solution';
const SITE_URL = 'https://reprintsolution.online';
const CONTACT_EMAIL = 'info@reprintsolution.online';
const LAST_UPDATED = '1 June 2026';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#030812] text-white">
      <Navbar />
      <main>
        <section className="border-b border-white/10 bg-[linear-gradient(135deg,#07111f_0%,#0f1f3d_100%)] py-16">
          <div className="container mx-auto max-w-4xl px-4">
            <nav className="mb-6 flex items-center gap-2 text-sm text-slate-400">
              <Link href="/" className="hover:text-white transition">Home</Link>
              <span>/</span>
              <span className="text-white">Privacy Policy</span>
            </nav>
            <h1 className="text-3xl font-black text-white md:text-4xl">Privacy Policy</h1>
            <p className="mt-3 text-slate-400 text-sm">Last updated: {LAST_UPDATED}</p>
          </div>
        </section>

        <section className="py-14">
          <div className="container mx-auto max-w-4xl px-4">
            <div className="prose-policy space-y-8 text-sm leading-7 text-slate-300">

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">1. Introduction</h2>
                <p className="mt-3">
                  {SITE_NAME} (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) operates the website at {SITE_URL} (the &quot;Site&quot;). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Site. Please read this policy carefully. If you disagree with its terms, please discontinue use of the Site.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">2. Information We Collect</h2>
                <p className="mt-3">We may collect the following types of information:</p>
                <ul className="mt-3 space-y-2 list-disc list-inside">
                  <li><strong className="text-white">Personal Identification Information:</strong> Name, email address, phone number, and postal address when you contact us or place an enquiry.</li>
                  <li><strong className="text-white">Usage Data:</strong> Pages visited, time spent on pages, browser type, device type, IP address, and referring URLs collected automatically via cookies and analytics tools.</li>
                  <li><strong className="text-white">Communications:</strong> Messages you send us via contact forms or email.</li>
                </ul>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">3. How We Use Your Information</h2>
                <p className="mt-3">We use the information we collect to:</p>
                <ul className="mt-3 space-y-2 list-disc list-inside">
                  <li>Respond to your enquiries and provide customer support</li>
                  <li>Improve our website content and user experience</li>
                  <li>Analyse site traffic and usage patterns</li>
                  <li>Comply with legal obligations</li>
                  <li>Send transactional communications related to your enquiries</li>
                  <li>Serve relevant advertisements via Google Ads and Google AdSense</li>
                </ul>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">4. Cookies and Tracking Technologies</h2>
                <p className="mt-3">
                  We use cookies and similar tracking technologies to track activity on our Site and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier.
                </p>
                <p className="mt-3">
                  <strong className="text-white">Google Analytics:</strong> We use Google Analytics to analyse how visitors use our Site. Google Analytics collects information such as how often users visit the Site, what pages they visit, and what other sites they used prior to coming to our Site. We use this information to improve our Site. Google Analytics collects only the IP address assigned to you on the date you visit the Site, not your name or other identifying information. We do not combine the information collected through Google Analytics with personally identifiable information.
                </p>
                <p className="mt-3">
                  <strong className="text-white">Google AdSense:</strong> We use Google AdSense to display advertisements on our Site. Google AdSense uses cookies to serve ads based on a user&apos;s prior visits to our website or other websites. Google&apos;s use of advertising cookies enables it and its partners to serve ads based on your visit to our Site and/or other sites on the Internet. You may opt out of personalised advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 underline">Google Ads Settings</a>.
                </p>
                <p className="mt-3">You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Site.</p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">5. Third-Party Disclosure</h2>
                <p className="mt-3">
                  We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties except as described in this policy. This does not include trusted third parties who assist us in operating our website, conducting our business, or servicing you, so long as those parties agree to keep this information confidential.
                </p>
                <p className="mt-3">
                  We may share information with third-party service providers including Google (Analytics, AdSense), Supabase (database infrastructure), and other operational tools. These providers are bound by their own privacy policies.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">6. Data Retention</h2>
                <p className="mt-3">
                  We retain personal information only for as long as necessary to fulfil the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">7. Your Rights</h2>
                <p className="mt-3">Depending on your location, you may have the following rights regarding your personal data:</p>
                <ul className="mt-3 space-y-2 list-disc list-inside">
                  <li>The right to access personal data we hold about you</li>
                  <li>The right to request correction of inaccurate data</li>
                  <li>The right to request deletion of your personal data</li>
                  <li>The right to object to processing of your personal data</li>
                  <li>The right to data portability</li>
                  <li>The right to withdraw consent at any time</li>
                </ul>
                <p className="mt-3">To exercise these rights, please contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-violet-400 hover:text-violet-300 underline">{CONTACT_EMAIL}</a>.</p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">8. Children&apos;s Privacy</h2>
                <p className="mt-3">
                  Our Site is not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">9. Changes to This Policy</h2>
                <p className="mt-3">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page with an updated &quot;Last updated&quot; date. You are advised to review this Privacy Policy periodically for any changes.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">10. Contact Us</h2>
                <p className="mt-3">
                  If you have any questions about this Privacy Policy, please contact us at:<br />
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-violet-400 hover:text-violet-300 underline">{CONTACT_EMAIL}</a>
                </p>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
